import React, { useEffect, useMemo, useRef, useState } from 'react';
import { generatePath, useHistory, useParams } from 'react-router-dom';
import { useErrorHandler } from 'react-error-boundary';

import {
  Col,
  Container,
  Dialog,
  ElmoSortIcon,
  Heading,
  LoadingOverlay,
  Radio,
  RadioButtons,
  Row,
  Spinner,
  SwapHorizIcon,
} from 'elmo-elements';

import { appData } from '@/lib/data';
import {
  getCurrentFrame,
  getPublishedEntityLocation,
  hasVocamData,
  processQuestion,
  replaceContentAssetLink,
  useIsMobileScreenSize,
} from '@/lib/util';
import ReviewRoutes, { ReviewParamType } from './routes';
import { useThemeStyle } from '@/modules/hooks/useThemeStyle';
import {
  useGetManifestQuery,
  useGetSignedCookiesQuery,
  useGetElearningJsonQuery,
  useGetQuizJsonQuery,
  useGetHelpJsonQuery,
  useGetCurrentTmsReviewerQuery,
  useStartReviewMutation,
  useGetElearningBehaviourSettingsQuery,
  useGetVideoLinkQuery,
} from '@/modules/services';
import { FaqType, HelperType } from '@/modules/types/helper';

import PlayerUi from '@/components/Player';
import ReviewComment from './components/ReviewComment';

import './Review.scss';
import { ReviewerStatus } from '@/modules/types/reviewers';
import ReviewComplete from '@/pages/Review/components/ReviewComplete';
import { useResumeElearning } from '@/modules/hooks/useResumeElearning';
import { useModuleStyle } from '@/modules/hooks/useModuleStyle';
import { AnswerDataType } from '@/modules/types/question';

const Review = (): React.ReactElement => {
  const { tenantId, elearningId, type, themeId, frameId } =
    useParams<ReviewParamType>();
  const history = useHistory();
  const childRef = useRef<{
    isCommentSaved: () => boolean;
    saveCurrentSlideComment: () => Promise<string | undefined>;
  }>(null);
  const handleError = useErrorHandler();
  const [isWrapped, setIsWrapped] = useState<boolean>(false);
  const [currentFrameId, setCurrentFrameId] = useState(frameId || '');
  const [isQuizReady, setIsQuizReady] = useState(false);
  const initialFrameId = useRef(frameId);
  const {
    data: elearningBehaviourSettingsData,
    isLoading: elearningBehaviourSettingsDataIsLoading,
  } = useGetElearningBehaviourSettingsQuery();
  const isBookmarkSettingsEnabled =
    elearningBehaviourSettingsData &&
    elearningBehaviourSettingsData.elearningBookmarkEnable;
  const showResumeModal = initialFrameId.current && isBookmarkSettingsEnabled;

  const [startReview] = useStartReviewMutation();

  const { data: reviewerData, isLoading: reviewerDataIsLoading } =
    useGetCurrentTmsReviewerQuery(elearningId);

  const { data: manifestData } = useGetManifestQuery(
    {
      type,
      tenantId,
      elearningId,
    },
    {
      skip: !reviewerData || reviewerData.status === ReviewerStatus.COMPLETED,
    },
  );

  const { isSuccess } = useGetSignedCookiesQuery(
    {
      tenantId,
      type,
      elearningId,
      ...(manifestData?.Item?.elearningIdentifier && {
        elearningIdentifier: `${manifestData?.Item?.elearningIdentifier}`,
      }),
      ...(manifestData?.Item?.quizIdentifier && {
        quizIdentifier: `${manifestData?.Item?.quizIdentifier}`,
      }),
      ...(manifestData?.Item?.elearningIdentifier && {
        themeId: `${manifestData?.Item?.themeUUID}`,
      }),
    },
    { skip: !manifestData || !manifestData.Item },
  );

  const {
    data: elearningData,
    isLoading: elearningIsLoading,
    isError: elearningIsError,
    error: elearningError,
  } = useGetElearningJsonQuery(
    {
      tenantId,
      type,
      entityId: elearningId,
      identifier: manifestData?.Item?.elearningIdentifier,
      entity: 'elearnings',
      contentType: 'published-content',
    },
    { skip: !isSuccess || !manifestData?.Item },
  );

  const currentFrame = useMemo(
    () => getCurrentFrame(elearningData?.frames ?? [], currentFrameId ?? ''),
    [elearningData?.frames, currentFrameId],
  );
  const { data: vocamVideoLink } = useGetVideoLinkQuery(currentFrameId ?? '', {
    skip: !currentFrameId || !hasVocamData(currentFrame),
  });

  const { data: quizData } = useGetQuizJsonQuery(
    {
      tenantId,
      type,
      entityId: elearningId,
      identifier: manifestData?.Item?.elearningIdentifier,
      entity: 'quizzes',
      contentType: 'published-content',
    },
    { skip: !isSuccess || !manifestData?.Item },
  );
  // passing undefined queryArgs as we don't need them but need to pass the queryOptions to skip requests until we have signed cookies
  const { data: helpData } = useGetHelpJsonQuery(undefined, {
    skip: !isSuccess,
  });
  const ResumeModalView: JSX.Element | null = useResumeElearning(
    setCurrentFrameId,
    elearningData?.name,
    showResumeModal,
  );

  const { isMobile } = useIsMobileScreenSize();

  useThemeStyle(elearningData?.theme);
  useModuleStyle(elearningData);

  useEffect(() => {
    if (frameId || !elearningData) return;
    if (!elearningData.frames?.length) return;

    // redirect to first frame if frameId param is empty
    history.replace(
      generatePath(ReviewRoutes.path, {
        tenantId,
        elearningId,
        type,
        frameId: elearningData.frames[0].id,
      }),
    );
  }, [frameId, tenantId, elearningId, history, type, elearningData]);

  useEffect(() => {
    if (!elearningBehaviourSettingsDataIsLoading && !showResumeModal) {
      setCurrentFrameId('');
    }
  }, [elearningBehaviourSettingsDataIsLoading, showResumeModal]);
  useEffect(() => {
    if (
      elearningData &&
      elearningData.theme &&
      !document.getElementById('ReviewCustomStyle')
    ) {
      const style = document.createElement('style');
      style.setAttribute('id', 'ReviewCustomStyle');
      document.head.appendChild(style);
      const stylesheet = style.sheet;
      (stylesheet as CSSStyleSheet).insertRule(
        `.review--layout .cke_contents {height:${
          elearningData.theme.windowMetadata.height - 336
        }px!important;}`,
        0,
      );
      (stylesheet as CSSStyleSheet).insertRule(
        `.review--layout.is-column .review--comment-col {max-width:${elearningData.theme.windowMetadata.width}px;}`,
        0,
      );

      // Mark the reviewer status as in progress when elearning data is fetched and rendered
      if (reviewerData && reviewerData.status === ReviewerStatus.NOT_STARTED) {
        startReview(elearningId);
      }
    }
  }, [elearningData]);

  if (elearningIsError) {
    handleError(elearningError);
  }

  // display completed page if reviewer has already completed the review
  if (reviewerData && reviewerData.status === ReviewerStatus.COMPLETED) {
    return <ReviewComplete />;
  }

  /* TODO: add unit test for logic flow */
  if (
    !manifestData ||
    !manifestData.Item ||
    !isSuccess ||
    !elearningData ||
    !elearningData.frames ||
    !quizData ||
    !elearningBehaviourSettingsData
  ) {
    return <Spinner />;
  }

  const playerHandlerState = {
    questionsToAsk: quizData.quizMetadata.questionNo,
    questionsAsked: 0,
    correctAnswers: 0,
    modifiedQuestions: quizData.questions,
    questionsCompleted: [],
  };

  const moveToFrame = (nextFrameId: string) => {
    setCurrentFrameId(nextFrameId);
    const nextPath = generatePath(ReviewRoutes.path, {
      tenantId,
      elearningId,
      frameId: nextFrameId,
      type,
    });
    history.push(nextPath);
  };

  const playerHandlers = {
    submitResult: (result: any, callback: () => void) => {
      callback();
    },
    submitQuestion: (
      answer: AnswerDataType,
      callback: (payload: any) => void,
    ) => {
      const nextPayload = processQuestion(answer, playerHandlerState);
      callback(nextPayload);
    },
    getQuestions: () => {
      playerHandlerState.questionsAsked = 0;
      playerHandlerState.correctAnswers = 0;
      playerHandlerState.modifiedQuestions = quizData.questions;
      playerHandlerState.questionsCompleted = [];
    },
    submitAssessment: (
      playerQuizData: any,
      callback: (payload: any) => void,
    ) => {
      const divisor = quizData?.quizMetadata.questionNo || 1;
      const score = Math.round(
        (playerHandlerState.correctAnswers / divisor) * 100,
      );

      callback({ score });
    },
    startAssessment: () => {
      setIsQuizReady(true);
    },
    moveToFrame: (nextFrameId: string) => {
      // check if there is unsaved comment
      const isCommentSaved = childRef.current?.isCommentSaved();

      if (!isCommentSaved) {
        Dialog.confirm({
          title: 'Unsaved changes',
          content:
            'This page has unsaved changes. Are you sure you want to leave this page?',
          async onOk() {
            // save comment
            const error = await childRef.current?.saveCurrentSlideComment();
            if (error) return;
            moveToFrame(nextFrameId);
          },
          okLabel: 'Save & Continue',
          cancelLabel: 'Cancel',
        });
      } else {
        moveToFrame(nextFrameId);
      }
    },
  };

  const publishedAssetLink = getPublishedEntityLocation({
    contentType: 'published-content-assets',
    tenantId,
    type: 'review',
    entity: 'elearnings',
    entityId: elearningId,
    identifier: manifestData.Item.elearningIdentifier,
  });

  const frames = elearningData.frames.map((frame) => {
    const content = replaceContentAssetLink({
      content: frame.content,
      newLink: publishedAssetLink,
      tenantId,
      elearningId,
    });
    return { ...frame, content };
  });

  const elearning = { ...elearningData };
  if (!elearning.theme) {
    elearning.theme = appData.playerTheme.default;
  }

  const handleChangeWrap = (e: string | number) => {
    setIsWrapped(e === 'wrap');
  };

  if (!reviewerData || reviewerDataIsLoading)
    return <div className="loading" />;

  return (
    <>
      {ResumeModalView}
      <LoadingOverlay isLoading={elearningIsLoading || reviewerDataIsLoading}>
        <Row
          wrap={isWrapped ? 'wrap' : 'nowrap'}
          direction={isWrapped ? 'column' : 'row'}
          className={`review review--layout ${
            isWrapped ? 'is-column' : 'is-row'
          }`}
          isNoGutters={true}
        >
          <Col className="" isAuto={true} grow={0}>
            <PlayerUi
              isPlayable={true}
              currentFrameId={currentFrameId || frames[0].id}
              isQuizEnabled={isQuizReady}
              elearning={elearning}
              frames={frames}
              quiz={quizData}
              resources={elearning.resources || []}
              helperContent={
                helpData
                  ? helpData.content
                  : (appData.helpJson.default.content as HelperType[])
              }
              helperFaq={
                helpData
                  ? helpData.faqs
                  : (appData.helpJson.default.faqs as FaqType[])
              }
              handlers={playerHandlers}
              isMobileView={isMobile}
              vocamVideoLink={vocamVideoLink}
              elearningBehaviourSettings={elearningBehaviourSettingsData}
            />
          </Col>
          <Col className="review--comment-col" grow={0} isAuto={true}>
            <div className="review--comment">
              <Container>
                <Row wrap="wrap">
                  <Col grow={0}>
                    <Row wrap="nowrap" alignItems="center" className="mb-4">
                      <Col isFill={true}>
                        <Heading>Comments</Heading>
                      </Col>
                      <Col isAuto={true} align="end">
                        <RadioButtons
                          selected={isWrapped ? 'wrap' : 'nowrap'}
                          type="outlined"
                          onChange={(e) => handleChangeWrap(e)}
                        >
                          <Radio value="nowrap">
                            <ElmoSortIcon />
                          </Radio>
                          <Radio value="wrap">
                            <SwapHorizIcon />
                          </Radio>
                        </RadioButtons>
                      </Col>
                    </Row>
                  </Col>
                  <Col grow={1}>
                    <ReviewComment ref={childRef} />
                  </Col>
                </Row>
              </Container>
            </div>
          </Col>
        </Row>
      </LoadingOverlay>
    </>
  );
};

export default Review;
